import React from "react";
import { getStock, type ParamsStockIn, setStockStatus, type StockData, stockIn, stockOut, stockTransfer } from "../../api/stock";
import { MDBBadge, MDBBtn, MDBDropdown, MDBDropdownItem, MDBDropdownMenu, MDBDropdownToggle, MDBInput, MDBInputGroup, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBTable, MDBTableBody, MDBTableHead, MDBTooltip } from "mdb-react-ui-kit";
import useLocale from "../../util/i18n";
import Toast from "../../component/toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSkus, getStockQty, type SkuQty } from "../../api/erp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft, faBan, faBars, faClockRotateLeft, faCodeCompare, faCog, faDolly, faPlus, faRotateBack, faXmark } from "@fortawesome/free-solid-svg-icons";
import { usePayload } from "../../util/token";
import BigNumber from "bignumber.js";
import ExcelJS from "exceljs";
import TagInfo from "../../util/tag";
import Autocomplete from "../../component/autocomplete";
import { format, startOfDay } from "date-fns";
import Select from "../../component/select";
import { GridContext } from "./_misc";
import { assertNumber } from "../../util/misc";

type CellData = StockData & { children?: Array<StockData> };

export default function Stock(props: { area: number, setMark: (status: number, value: number) => void }) {
    let { 0: data, 1: setData } = React.useState<Record<string, Array<CellData>>>({});
    let { 0: stock, 1: setStock } = React.useState<Record<string, SkuQty>>({});
    let { 0: mark, 1: setMark } = React.useState('');
    let { 0: payload } = usePayload();
    let { 0: __ } = useLocale();
    let href = useLocation();
    let { 0: configs, 1: saveConfigs } = React.useContext(GridContext);
    let picking = React.useMemo(() => configs[props.area].picking ?? [], [props.area, configs]);
    let lookup = React.useCallback((sku: string, e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setMark(old => old === sku ? '' : sku);
    }, [setMark]);
    let setStatus = React.useCallback((key: string, index: number) => {
        setData(old => {
            let cloned = { ...old }, bak = { ...old };
            if (cloned[key][index]) {
                let current = cloned[key][index].status;
                cloned[key][index].status = current > 0 ? 0 : (payload!.role - 128 ? 1 : 2);
                setStockStatus({
                    area: cloned[key][index].area,
                    row: cloned[key][index].row,
                    col: cloned[key][index].col,
                    sku: cloned[key][index].sku,
                    status: cloned[key][index].status
                }).then(() => {
                    if (current > 0) {
                        props.setMark(current, -1);
                    } else {
                        props.setMark(cloned[key][index].status, 1);
                    }
                }, () => {
                    Toast.show(__('An error detected. Please try again later.'), 'danger');
                    setData(bak);
                })
            }
            return cloned;
        });
    }, [setData, props.setMark, __, payload]);
    let afterStockIn = React.useCallback((params: ParamsStockIn) => {
        setData(old => {
            let cloned = { ...old }, key = params.row + 'x' + params.col;
            cloned[key] ??= [];
            for (let sku in params.sku) {
                let flag = true;
                let item: CellData = {
                    ...params, sku: sku, qty: params.sku[sku],
                    status: 0, children: []
                };
                for (let i in cloned[key]) {
                    if (cloned[key][i].sku === sku) {
                        cloned[key][i].qty += params.sku[sku]; console.log(i);
                        for (let j in (cloned[key][i].children) ?? []) {
                            if (cloned[key][i].children![j].created_at === item.created_at) {
                                cloned[key][i].children![j].qty += params.sku[sku];
                                flag = false;
                                break;
                            }
                        }
                        if (flag) {
                            cloned[key][i].children ??= [];
                            cloned[key][i].children!.push(item);
                            flag = false;
                        }
                        break;
                    }
                }
                if (flag === true) {
                    item.children!.push(item);
                    cloned[key].push(item);
                }
            }
            return cloned;
        });
    }, [setData]);
    let afterStockOut = React.useCallback((params: Array<[number, number, number, string, number] | [number, number, number, string]>) => {
        setData(old => {
            let cloned = { ...old };
            for (let { 1: row, 2: col, 3: sku, 4: qty } of params) {
                let key = row + 'x' + col;
                let filtered = [], flag = true;
                for (const item of (cloned[key] ?? [])) {
                    if (item.sku !== sku) {
                        filtered.push(item);
                    } else if (flag && qty) {
                        if (item.qty <= qty) {
                            qty -= item.qty;
                        } else {
                            filtered.push({ ...item, qty: item.qty - qty });
                            flag = false;
                        }
                    }
                }
                cloned[key] = filtered;
            }
            return cloned;
        });
    }, [setData]);
    let afterTransfer = React.useCallback((from: [number, number, number, string, number, number], to: [number, number, number]) => {
        setData(old => {
            let cloned = { ...old };
            let key = from[1] + 'x' + from[2];
            for (const i in (cloned[key] ?? [])) {
                if (cloned[key][i].sku === from[3]) {
                    cloned[key][i].qty -= from[4];
                    if (cloned[key][i].qty > 0) {
                        for (let j in (cloned[key][i].children ?? [])) {
                            if (cloned[key][i].children![j].created_at === from[5]) {
                                if (cloned[key][i].children![j].qty === from[4]) {
                                    cloned[key][i].children!.splice(parseInt(j), 1);
                                } else {
                                    cloned[key][i].children![j].qty -= from[4];
                                }
                                break;
                            }
                        }
                    } else {
                        cloned[key].splice(parseInt(i), 1);
                    }
                    break;
                }
            }
            if (to[0] === from[0]) {
                key = to[1] + 'x' + to[2];
                cloned[key] ??= [];
                let flag: boolean | `${number}` = true;
                for (const i in (cloned[key] ?? [])) {
                    if (cloned[key][i].sku === from[3]) {
                        cloned[key][i].qty += from[4];
                        flag = i as `${number}`;
                        for (let j in (cloned[key][i].children) ?? []) {
                            if (cloned[key][i].children![j].created_at === from[5]) {
                                cloned[key][i].children![j].qty += from[4];
                                flag = false;
                                break;
                            }
                        }
                        break;
                    }
                }
                if (flag) {
                    let item: CellData = {
                        area: to[0], row: to[1], col: to[2],
                        sku: from[3], qty: from[4], created_at: from[5],
                        status: 0, children: []
                    };
                    if (typeof flag !== 'boolean') {
                        cloned[key][flag].children ??= [];
                        cloned[key][flag].children!.push(item);
                    } else {
                        item.children!.push(item);
                    }
                    cloned[key].push(item);
                }
            }
            return cloned;
        });
    }, [setData]);
    const manager = React.useRef<ManagerRef>(null);
    const pickingManager = React.useRef<ManagerRef>(null);
    let manage = React.useCallback((row: number, col: number, e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();
        manager.current?.([props.area, row, col], data[row + 'x' + col] ?? []);
    }, [props.area, data]);
    let managePicking = React.useCallback((row: number, col: number, e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();
        pickingManager.current?.([props.area, row, col], data[row + 'x' + col] ?? []);
    }, [props.area, data]);
    let setPickingArea = React.useCallback((row: number, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let cloned = { ...configs[props.area] };
        cloned.picking ??= [];
        let p = cloned.picking.indexOf(row);
        if (p === -1) {
            cloned.picking.push(row);
        } else {
            cloned.picking.splice(p, 1);
        }
        saveConfigs(cloned, props.area);
    }, [props.area, saveConfigs, configs]);
    React.useEffect(() => {
        getStock({ area: props.area }).then(response => {
            if (typeof response !== 'string') {
                let data: Record<string, Record<string, CellData>> = {};
                for (let i of (response.data || [])) {
                    let key = i.row + 'x' + i.col;
                    data[key] ??= {};
                    i.qty = parseFloat(i.qty.toString());
                    if (data[key][i.sku]) {
                        data[key][i.sku].qty += i.qty;
                        data[key][i.sku].status = Math.max(data[key][i.sku].status, i.status);
                        data[key][i.sku].children?.push(i);
                    } else {
                        data[key][i.sku] = i;
                        data[key][i.sku].children = [{ ...i }];
                    }
                }
                let result: Record<string, Array<CellData>> = {};
                for (let key in data) {
                    result[key] = Object.values(data[key]);
                }
                setData(result);
            }
        });
    }, [props.area, setData]);
    React.useEffect(() => {
        let skus: Record<string, 1> = {};
        for (let p in data) {
            if (typeof data?.[p]?.[0]?.row !== 'undefined' &&
                configs[props.area].picking?.includes(assertNumber(data[p][0].row))) {
                for (let item of data[p]) {
                    skus[item.sku] = 1;
                }
            }
        }
        let sku = Object.keys(skus);
        if (sku.length) {
            let map: Record<string, SkuQty> = {};
            getStockQty({ filter: { sku } }).then(response => {
                if (typeof response !== 'string') {
                    for (let item of response.data) {
                        item.aq && (item.aq = parseFloat(item.aq.toString()));
                        item.qty && (item.qty = parseFloat(item.qty.toString()));
                        map[item.sku] = item;
                    }
                    setStock(map);
                }
            })
        }
    }, [data, setStock, configs, props.area]);
    React.useEffect(() => {
        let l = () => {
            getStock({ area: props.area }).then(async response => {
                if (typeof response !== 'string') {
                    let data = response.data;
                    data.sort((a, b) => a.row - b.row || a.col - b.col);
                    let picks: Record<string, true> = {}, sum: Record<string, BigNumber> = {}, fixed: Record<string, number> = {};
                    let ps: Array<Promise<void>> = [];
                    for (let item of data) {
                        ps.push(new Promise(resolve => {
                            TagInfo.fromEpc(item.sku, tag => {
                                let sku = tag.sku;
                                if (picking.includes(item.row) === true) {
                                    picks[sku] = true;
                                } else {
                                    sum[sku] = (sum[sku] ?? new BigNumber(0)).minus(item.qty);
                                }
                                resolve();
                            });
                        }));
                    }
                    ps.length && await Promise.all(ps);
                    let sku = Object.keys(picks);
                    if (sku.length) {
                        for (let item of (await getStockQty({ filter: { sku } }) as { data: Array<SkuQty> })?.data ?? []) {
                            fixed[item.sku] = (sum[item.sku] ?? new BigNumber(0)).plus(item.qty).toNumber();
                        }
                    }
                    const file = new ExcelJS.Workbook();
                    const sheet = file.addWorksheet(configs[props.area].name || __('Area %d').replace('%d', (props.area + 1).toString()));
                    let row = sheet.getRow(1);
                    row.getCell(1).value = __('Row');
                    row.getCell(2).value = __('Column');
                    row.getCell(3).value = 'SKU';
                    row.getCell(4).value = __('Qty');
                    for (let item of data) {
                        let row = sheet.getRow(sheet.rowCount + 1), isPicking = picking.includes(item.row) === true, sku = item.sku;
                        row.getCell(1).value = (parseInt(item.row.toString()) + 1).toString() + (isPicking ? '[P]' : '');
                        row.getCell(2).value = parseInt(item.col.toString()) + 1;
                        row.getCell(3).value = sku;
                        row.getCell(4).value = isPicking ? (fixed[sku] || 0) : parseFloat(item.qty.toString());
                        if (isPicking) {
                            if (item.status === 1) {
                                row.getCell(4).style = { font: { color: { argb: 'FF33CC66' } } };
                            } else if (item.status === 2) {
                                row.getCell(4).style = { font: { color: { argb: 'FFE4A11B' } } };
                            } else if ((fixed[sku] || 0) <= 0) {
                                row.getCell(4).style = { font: { color: { argb: 'FFCC3333' } } };
                            }
                        }
                    }
                    file.xlsx.writeBuffer().then(buffer => {
                        let a = document.createElement('a');
                        a.download = 'export.xlsx';
                        a.href = URL.createObjectURL(new Blob([buffer]));
                        document.body.append(a);
                        a.click();
                        URL.revokeObjectURL(a.href);
                        a.remove();
                    });
                }
            });
        };
        document.addEventListener('export', l);
        return () => {
            document.removeEventListener('export', l);
        };
    }, [configs, props.area]);
    React.useEffect(() => {
        if (href.hash) {
            window.setTimeout(() => {
                let cell = document.getElementById(href.hash.substring(1));
                let box = document.querySelector('.stock-grid .table-responsive') as HTMLElement;
                if (cell && box) {
                    box.scrollLeft = cell.offsetWidth * parseInt(href.hash.substring(href.hash.indexOf('x') + 1)) - box.offsetWidth / 2;
                }
            }, 600);
        }
    }, [href]);
    return (
        <div className="stock-grid" data-exportable="1" data-searchable="1">
            <MDBTable className="bg-white text-center mb-0" bordered striped small responsive>
                <colgroup>
                    <col width="1" />
                    {(new Array(configs[props.area].cols)).fill(1).map((_, j) => (<col key={'c' + j} />))}
                </colgroup>
                <MDBTableHead>
                    <tr>
                        <th>
                            {configs[props.area].name || __('Area %d').replace('%d', (props.area + 1).toString())}
                            <Link to={'/bin/history?area=' + props.area} className="history" title={__('History')}>
                                <FontAwesomeIcon icon={faClockRotateLeft} />
                            </Link>
                        </th>
                        {(new Array(configs[props.area].cols)).fill(1).map((_, j) => (
                            <th key={'h' + j} style={{ width: 100 / configs[props.area].cols + '%' }}>
                                {j + 1}
                                <Link to={'/bin/history?area=' + props.area + '&col=' + j} className="history" title={__('History')}>
                                    <FontAwesomeIcon icon={faClockRotateLeft} />
                                </Link>
                            </th>
                        ))}
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {(new Array(configs[props.area].rows)).fill(1).map((_, i) => {
                        let row = configs[props.area].rows - i - 1;
                        return (
                            <tr key={(row + 1).toString()}>
                                <th>
                                    {row + 1}
                                    {configs[props.area].picking?.includes(row) ? (
                                        <button type="button" title={__('Picking Area')} onClick={setPickingArea.bind(null, row)} style={{ opacity: 1 }}>
                                            <FontAwesomeIcon icon={faCodeCompare} />
                                        </button>
                                    ) : (
                                        <>
                                            <Link to={'/bin/history?area=' + props.area + '&row=' + row} className="history" title={__('History')}>
                                                <FontAwesomeIcon icon={faClockRotateLeft} />
                                            </Link>
                                            <button type="button" title={__('Picking Area')} onClick={setPickingArea.bind(null, row)}>
                                                <FontAwesomeIcon icon={faCodeCompare} />
                                            </button>
                                        </>
                                    )}
                                </th>
                                {(new Array(configs[props.area].cols)).fill(1).map((_, j) => {
                                    let key = row + 'x' + j;
                                    let picking = configs[props.area].picking?.includes(row) === true;
                                    return picking ? (
                                        <PickingArea key={key} row={row} col={j} data={data[key] ?? []} stock={stock} mark={mark} lookup={lookup} setStatus={setStatus.bind(null, row + 'x' + j)} manage={managePicking.bind(null, row, j)} />
                                    ) : (
                                        <Cell key={key} area={props.area} row={row} col={j} data={data[key] ?? []} mark={mark} manage={manage.bind(null, row, j)} />
                                    );
                                })}
                            </tr>
                        );
                    })}
                </MDBTableBody>
            </MDBTable>
            <Manager area={props.area} afterStockIn={afterStockIn} afterStockOut={afterStockOut} afterTransfer={afterTransfer} ref={manager} />
            <PickingManager area={props.area} afterStockIn={afterStockIn} afterStockOut={afterStockOut} ref={pickingManager} />
        </div >
    );
}

function PickingArea(props: { row: number, col: number, stock: Record<string, SkuQty>, data: Array<CellData>, mark: string, lookup: (sku: string, e: React.MouseEvent<any>) => void, setStatus: (i: number) => void, manage: (e: React.MouseEvent<any>) => void }) {
    let { 0: __ } = useLocale();
    return (
        <td id={props.row + 'x' + props.col}>
            {props.data.map((item, i) => (
                <React.Fragment key={item.sku}>
                    {i === 0 ? null : (<hr />)}
                    <span className={'picking ' +
                        (item.status == 2 ? 'text-warning' :
                            (item.status == 1 ? 'text-success' :
                                (props.mark === item.sku ? 'text-info' : '')))} onClick={props.setStatus.bind(null, i)}>
                        {item.sku}
                    </span>
                    <MDBBadge className="stock">
                        {props.stock[item.sku] ? ((typeof props.stock[item.sku].aq !== 'undefined' ? props.stock[item.sku].aq + '/' : '') + props.stock[item.sku].qty) : 'N/A'}
                        <span className="popup">
                            {__('Available Quantity')}: {props.stock[item.sku]?.aq ?? 'N/A'}<br />{__('Stock on Hand')}: {props.stock[item.sku]?.qty ?? 'N/A'}
                        </span>
                    </MDBBadge>
                </React.Fragment>
            ))}
            <button type="button" className="config" onClick={props.manage} title={__('Config')}>
                <FontAwesomeIcon icon={faCog} />
            </button>
        </td>
    );
}

function Cell(props: { area: number, row: number, col: number, data: Array<CellData>, mark: string, manage: (e: React.MouseEvent<any>) => void }) {
    let { 0: __ } = useLocale();
    let { 0: configs, 1: saveConfigs } = React.useContext(GridContext);
    let navigate = useNavigate();
    let doEnable = React.useCallback(() => {
        let cloned = { ...configs[props.area] };
        cloned.disabled ??= [];
        let target = cloned.disabled.indexOf(props.row + 'x' + props.col);
        target > -1 && cloned.disabled.splice(target, 1);
        saveConfigs(cloned, props.area);
    }, [saveConfigs, configs, props.row, props.col]);
    let doDisable = React.useCallback(() => {
        let cloned = { ...configs[props.area] };
        cloned.disabled ??= [];
        cloned.disabled.push(props.row + 'x' + props.col);
        saveConfigs(cloned);
    }, [saveConfigs, configs, props.row, props.col]);
    let history = React.useCallback(() => {
        navigate('/bin/history?area=' + props.area + '&row=' + props.row + '&col=' + props.col);
    }, [props.area, props.row, props.col]);
    return (
        <td id={props.row + 'x' + props.col}>
            {(configs[props.area].disabled || []).includes(props.row + 'x' + props.col) ? (
                <button type="button" title={__('Disabled')} className="banned" onClick={doEnable}>
                    <FontAwesomeIcon icon={faBan} />
                </button>
            ) : (props.data.length ? (
                <>
                    {props.data.map((item, i) => (
                        <React.Fragment key={item.sku}>
                            {i === 0 ? null : (<hr />)}
                            <span className={item.qty < 0 ? 'text-danger' : (props.mark === item.sku ? 'text-info' : undefined)}>
                                {item.sku || (<FontAwesomeIcon icon={faXmark} size="2x" className="text-danger" />)}
                            </span>
                            <MDBBadge>{item.qty}</MDBBadge>
                        </React.Fragment>
                    ))}
                </>
            ) : null)}
            <MDBDropdown dropup>
                <MDBDropdownToggle><FontAwesomeIcon icon={faBars} /></MDBDropdownToggle>
                <MDBDropdownMenu responsive="end">
                    <MDBDropdownItem>
                        <button type="button" onClick={props.manage}>
                            <FontAwesomeIcon icon={faDolly} />
                            {__('Manage')}
                        </button>
                    </MDBDropdownItem>
                    {props.data.length ? (<></>) : (
                        <MDBDropdownItem>
                            <button type="button" onClick={doDisable}>
                                <FontAwesomeIcon icon={faBan} />
                                {__('Disable')}
                            </button>
                        </MDBDropdownItem>
                    )}
                    <MDBDropdownItem>
                        <button type="button" onClick={history}>
                            <FontAwesomeIcon icon={faClockRotateLeft} />
                            {__('History')}
                        </button>
                    </MDBDropdownItem>
                </MDBDropdownMenu>
            </MDBDropdown>
        </td>
    );
}

type ManagerRef = (position: [number, number, number], items: Array<CellData>) => void;

interface ManagerProps {
    area: number;
    afterStockIn?: (params: ParamsStockIn) => void;
    afterStockOut?: (params: Array<[number, number, number, string, number]>) => void;
    afterTransfer?: (from: [number, number, number, string, number, number], to: [number, number, number]) => void;
}

const year = (new Date()).getFullYear();

const Manager = React.forwardRef<ManagerRef, ManagerProps>(function (props, ref) {
    let { 0: params, 1: setParams } = React.useState<Array<CellData & { diff?: number }> | null>(null);
    let { 0: transfer, 1: setTransfer } = React.useState<number>(-1);
    let { 0: configs } = React.useContext(GridContext);
    const position = React.useRef<[number, number, number]>();
    let { 0: coords, 1: setCoords } = React.useState('');
    let { 0: __, 3: locale } = useLocale();
    const forms = React.useRef<Array<HTMLAllCollection | null>>([]);
    let doStockIn = React.useCallback((i: number, e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();
        //@ts-ignore
        if (forms.current?.[i] && !forms.current[i].reportValidity()) {
            return;
        }
        if (!params?.[i]?.sku) {
            Toast.show(__('The %s is invalid.').replace('%s', 'SKU'));
            return;
        }
        if ((params?.[i]?.diff ?? 0) <= 0) {
            Toast.show(__('The %s is invalid.').replace('%s', __('Qty')));
            return;
        }
        let cloned = { area: params[i].area, row: params[i].row, col: params[i].col, sku: { [params[i].sku]: params[i].diff || 0 }, created_at: params[i].created_at || startOfDay(new Date()).getTime() / 1000 };
        stockIn(cloned).then(response => {
            if (typeof response !== 'string' && response.error) {
                Toast.show(__('An error detected. Please try again later.'), 'danger');
                return;
            }
            props.afterStockIn?.(cloned);
            setParams(old => {
                let result = [...old!], flag = true;
                for (let j in result) {
                    if (parseFloat(j) !== i && result[j].sku === result[i].sku && result[j].created_at === cloned.created_at) {
                        //result[j].qty += (result[i].diff || 0);
                        flag = false;
                        break;
                    }
                }
                if (flag) {
                    result[i].qty = result![i].diff || 0;
                    result[i].diff = 0;
                    result[i].created_at = cloned.created_at;
                } else {
                    result.splice(i, 1);
                }
                return result;
            });
        }, () => {
            Toast.show(__('An error detected. Please try again later.'), 'danger');
        });
    }, [params, setParams, props.afterStockIn, __]);
    let doStockOut = React.useCallback((i: number, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if ((params?.[i]?.diff ?? 0) <= 0 || (params![i].diff ?? 0) > params![i].qty) {
            Toast.show(__('The %s is invalid.').replace('%s', __('Qty')));
            return;
        }
        let position: Array<[number, number, number, string, number]> = [[params![i].area, params![i].row, params![i].col, params![i].sku, params![i].diff!]];
        stockOut({ position }).then(() => {
            props.afterStockOut?.(position);
            setParams(old => {
                let result = [...old!];
                try {
                    if (parseFloat(params![i].diff?.toString() || '0') === parseFloat(params![i].qty?.toString() || '0')) {
                        result.splice(i, 1);
                    } else {
                        result[i].qty -= params![i].diff || 0;
                    }
                } catch { }
                return result;
            });
        }, () => {
            Toast.show(__('An error detected. Please try again later.'), 'danger');
        });
    }, [setParams, params, __, props.afterStockOut]);
    let willTransfer = React.useCallback((i: number, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if ((params?.[i]?.diff ?? 0) <= 0 || (params![i].diff ?? 0) > params![i].qty) {
            Toast.show(__('The %s is invalid.').replace('%s', __('Qty')));
            return;
        }
        setTransfer(i);
    }, [setTransfer, params]);
    let doTransfer = React.useCallback((i: number, e: React.FormEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let data = new FormData(e.target as HTMLFormElement);
        let from: [number, number, number, string, number, number] = [params![i].area, params![i].row, params![i].col, params![i].sku, params![i].diff!, Math.floor(params![i].created_at!)];
        let to: [number, number, number] = [parseInt(data.get('area') as string), parseInt(data.get('row') as string), parseInt(data.get('col') as string)];
        stockTransfer(from, to).then(() => {
            props.afterTransfer?.(from, to);
            setParams(old => {
                let result = [...old!];
                try {
                    if (parseFloat(params![i].diff?.toString() || '0') === parseFloat(params![i].qty?.toString() || '0')) {
                        result.splice(i, 1);
                    } else {
                        result[i].qty -= params![i].diff || 0;
                    }
                } catch { }
                return result;
            });
        }, () => {
            Toast.show(__('An error detected. Please try again later.'), 'danger');
        });
    }, [setTransfer, params, props.afterTransfer]);
    let onChange = React.useCallback((idx: number, e: { target: { name?: string, value: string } }) => {
        setParams(old => {
            let cloned = old ? [...old] : [];
            cloned[idx] = {
                area: position.current![0],
                row: position.current![1],
                col: position.current![2],
                sku: cloned[idx]?.sku ?? '',
                qty: cloned[idx]?.qty ?? 0,
                diff: cloned[idx]?.diff,
                status: 0,
                created_at: cloned[idx]?.created_at ?? 0
            };
            if (e.target.name === 'sku') {
                cloned[idx].sku = e.target.value;
            } else if (e.target.name === 'created_at') {
                cloned[idx].created_at = Math.round((new Date(e.target.value)).getTime() / 1000);
            } else {
                cloned[idx][e.target.name as 'qty'] = parseFloat(e.target.value);
                Number.isNaN(cloned[idx][e.target.name as 'qty']) && (cloned[idx][e.target.name as 'sku'] = e.target.value);
            }
            return cloned;
        });
    }, [setParams]);
    let more = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setParams(old => {
            return [...old!, {
                area: position.current![0], row: position.current![1],
                col: position.current![2], isNew: true,
                sku: '', diff: 1, qty: 0, status: 0
            }];
        });
    }, [setParams]);
    React.useImperativeHandle(ref, () => ((p, items) => {
        position.current = p;
        setCoords(': ' + (parseFloat(p[1].toString()) + 1) + 'x' + (parseFloat(p[2].toString()) + 1));
        setTransfer(-1);
        if (items.length) {
            let details = [];
            for (let item of items) {
                if (item.children?.length) {
                    for (let child of item.children) {
                        details.push(child);
                    }
                } else {
                    details.push(item);
                }
            }
            setParams(details);
        } else {
            setParams([{
                area: p[0], row: p[1], col: p[2],
                sku: '', qty: 0, status: 0
            }]);
        }
    }
    ), [setParams, setTransfer, setCoords]);
    return (
        <MDBModal open={Boolean(params)} onClose={setParams.bind(null, null)}>
            <MDBModalDialog centered size="lg">
                <MDBModalContent>
                    <MDBModalHeader>
                        {configs[props.area]?.name ?? __('Area %d').replace('%d', props.area.toString())}{coords}
                    </MDBModalHeader>
                    <MDBModalBody className="bin-config">
                        {params?.length ? (params?.map((item, i) => {
                            let date = item.created_at ? new Date(item.created_at * 1000) : null;
                            return transfer === i ? (
                                <MDBInputGroup tag="form" className="mb-3" key={'row-' + i} onSubmit={doTransfer.bind(null, i)}>
                                    <span className="input-group-text">{item.sku}</span>
                                    <span className="input-group-text">{item.diff}</span>
                                    <Target current={item.area + ':' + item.row + 'x' + item.col} />
                                    <MDBBtn type="submit" color="info" className="px-3" title={__('Stock Transfer')}>
                                        <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                                    </MDBBtn>
                                    <MDBBtn type="button" color="secondary" className="px-3" title={__('Cancel')} onClick={setTransfer.bind(null, -1)}>
                                        <FontAwesomeIcon icon={faRotateBack} />
                                    </MDBBtn>
                                </MDBInputGroup>
                            ) : (
                                <MDBInputGroup tag="form" className="mb-3" key={'row-' + i} ref={e => forms.current[i] = e}>
                                    {item.qty === 0 ? (
                                        <>
                                            <Autocomplete label="SKU" name="sku" className="active" forceFill autoCapitalize="characters" load={getSkus} value={item.sku} onChange={onChange.bind(null, i)} />
                                            <MDBInput type="number" label={__('Qty')} name="diff" min="1" className="active" value={item.diff ?? 0} onChange={onChange.bind(null, i)} wrapperStyle={{ flexBasis: '5.5rem' }} />
                                            <MDBInput type="date" label={__('Date')} name="created_at" value={format(item.created_at ? item.created_at * 1000 : new Date(), 'yyyy-MM-dd')} wrapperStyle={{ flexBasis: '10rem' }} onChange={onChange.bind(null, i)} />
                                            <MDBBtn type="button" color="info" className="px-3" title={__('Stock In')} onClick={doStockIn.bind(null, i)}>
                                                <FontAwesomeIcon icon={faPlus} />
                                            </MDBBtn>
                                        </>
                                    ) : (
                                        <>
                                            <span className="input-group-text">{item.sku}</span>
                                            <MDBInput type="number" label={__('Outbound Qty')} name="diff" min="1" max={item.qty ?? 0} className="active" value={item.diff ?? 0} onChange={onChange.bind(null, i)} />
                                            <span className="input-group-text">/{parseFloat(item.qty.toString())}</span>
                                            {date ? (
                                                <span className="input-group-text" style={{ flexBasis: '7rem' }}>{format(date, date.getFullYear() !== year ? 'PPP' : 'LLL do', { locale })}</span>
                                            ) : null}
                                            <MDBBtn type="button" color="info" className="px-3" title={__('Stock Transfer')} onClick={willTransfer.bind(null, i)}>
                                                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                                            </MDBBtn>
                                            <MDBBtn type="button" color="danger" className="px-3" title={__('Stock Out')} onClick={doStockOut.bind(null, i)}>
                                                <FontAwesomeIcon icon={faDolly} />
                                            </MDBBtn>
                                        </>
                                    )}
                                </MDBInputGroup>
                            );
                        })) : (
                            <p className="m-0 text-center">{__('No data to display')}</p>
                        )}
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn type="button" color="info" onClick={more}>{__('Add More Rows')}</MDBBtn>
                        <MDBBtn type="button" color="secondary" onClick={setParams.bind(null, null)}>{__('Close')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
});

function Target(props: { current: string }) {
    let { 0: area, 1: setArea } = React.useState<string>('0');
    let { 0: row, 1: setRow } = React.useState<string>('0');
    let { 0: configs } = React.useContext(GridContext);
    let rows = React.useMemo(() => {
        let result = [], a = parseInt(area);
        for (let i = 0; i < (configs[a]?.rows ?? 0); i++) {
            if ((configs[a].picking ?? []).includes(i)) {
                continue;
            }
            result.push({ value: i.toString(), label: (i + 1).toString() });
        }
        return result;
    }, [area, configs]);
    let cols = React.useMemo(() => {
        let result = [], a = parseInt(area);
        for (let i = 0; i < (configs[a]?.cols ?? 0); i++) {
            if ((configs[a].disabled ?? []).includes(row + 'x' + i) || area + ':' + row + 'x' + i === props.current) {
                continue;
            }
            result.push({ value: i.toString(), label: (i + 1).toString() });
        }
        return result;
    }, [area, row, configs, props.current]);
    let { 0: __ } = useLocale();
    return (
        <>
            <Select name="area" label={__('Area')} options={configs.map((config, i) => {
                let label = config.name ?? __('Area %d').replace('%d', i.toString());
                return { label, value: i.toString() };
            })} value={area} onChange={setArea} />
            <Select name="row" label={__('Row')} options={rows} value={row} onChange={setRow} />
            <Select name="col" label={__('Column')} options={cols} />
        </>
    );
}

interface PickingManagerProps {
    area: number;
    afterStockIn?: (params: ParamsStockIn) => void;
    afterStockOut?: (params: Array<[number, number, number, string]>) => void;
}

const PickingManager = React.forwardRef<ManagerRef, PickingManagerProps>(function (props, ref) {
    let { 0: params, 1: setParams } = React.useState<Array<CellData> | null>(null);
    const position = React.useRef<[number, number, number]>();
    let { 0: coords, 1: setCoords } = React.useState('');
    let { 0: __ } = useLocale();
    let { 0: configs } = React.useContext(GridContext);
    let doStockIn = React.useCallback((i: number, e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!params?.[i]?.sku) {
            Toast.show(__('The %s is invalid.').replace('%s', 'SKU'));
            return;
        }
        let cloned = { area: params[i].area, row: params[i].row, col: params[i].col, sku: { [params[i].sku]: 1 } };
        stockIn(cloned).then(() => {
            props.afterStockIn?.(cloned);
            setParams(old => {
                let result = [...old!];
                result[i] = {
                    ...params![i],
                    sku: params![i].sku,
                    qty: 1
                };
                return result;
            });
        }, () => {
            Toast.show(__('An error detected. Please try again later.'), 'danger');
        });
    }, [params, setParams, props.afterStockIn]);
    let doStockOut = React.useCallback((i: number, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let position: Array<[number, number, number, string]> = [[params![i].area, params![i].row, params![i].col, params![i].sku]];
        stockOut({ position }).then(() => {
            props.afterStockOut?.(position);
            setParams(old => {
                let result = [...old!];
                result.splice(i, 1);
                return result;
            });
        }, () => {
            Toast.show(__('An error detected. Please try again later.'), 'danger');
        });
    }, [setParams, params, __, props.afterStockOut]);
    let onChange = React.useCallback((idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        setParams(old => {
            let cloned = old ? [...old] : [];
            cloned[idx] = {
                area: position.current![0],
                row: position.current![1],
                col: position.current![2],
                sku: cloned[idx]?.sku ?? '',
                qty: cloned[idx]?.qty ?? 1,
                status: 0
            };
            if (e.target.name === 'sku') {
                cloned[idx].sku = e.target.value;
            } else {
                cloned[idx][e.target.name as 'qty'] = parseFloat(e.target.value);
                Number.isNaN(cloned[idx][e.target.name as 'qty']) && (cloned[idx][e.target.name as 'sku'] = e.target.value);
            }
            return cloned;
        });
    }, [setParams]);
    let more = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setParams(old => {
            return [...old!, {
                area: position.current![0], row: position.current![1],
                col: position.current![2], isNew: true,
                sku: '', qty: 0, status: 0
            }];
        });
    }, [setParams]);
    React.useImperativeHandle(ref, () => ((p, items) => {
        position.current = p;
        setCoords(': ' + (parseFloat(p[1].toString()) + 1) + 'x' + (parseFloat(p[2].toString()) + 1));
        if (items.length) {
            setParams(items);
        } else {
            setParams([{
                area: p[0], row: p[1], col: p[2],
                sku: '', qty: 0, status: 0
            }]);
        }
    }
    ), [setParams]);
    return (
        <MDBModal open={Boolean(params)} onClose={setParams.bind(null, null)}>
            <MDBModalDialog centered>
                <MDBModalContent>
                    <MDBModalHeader>
                        {configs[props.area]?.name ?? __('Area %d').replace('%d', props.area.toString())}{coords} [P]
                    </MDBModalHeader>
                    <MDBModalBody className="bin-config">
                        {params?.map((item, i) => (
                            <MDBInputGroup className="mb-3" key={'row-' + i}>
                                {item.qty === 0 ? (
                                    <>
                                        <MDBInput label="SKU" name="sku" className="active" value={item.sku} onChange={onChange.bind(null, i)} />
                                        <MDBBtn type="button" color="info" className="px-3" onClick={doStockIn.bind(null, i)}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </MDBBtn>
                                    </>
                                ) : (
                                    <>
                                        <span className="input-group-text" style={{ flex: '1 1 0' }}>{item.sku}</span>
                                        <MDBBtn type="button" color="danger" className="px-3" onClick={doStockOut.bind(null, i)}>
                                            <FontAwesomeIcon icon={faDolly} />
                                        </MDBBtn>
                                    </>
                                )}
                            </MDBInputGroup>
                        ))}
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn type="button" color="info" onClick={more}>{__('Add More Rows')}</MDBBtn>
                        <MDBBtn type="button" color="secondary" onClick={setParams.bind(null, null)}>{__('Close')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
});
